import {
  AGREEMENT_TYPE,
  IAgreementTemplatetAggregator,
} from 'services/templates/agreement-template/types';
import { ICustomSectionTemplateMutation } from 'services/templates/section-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionMutation,
  IDistributionSharesMutation,
  IDistributionType,
  SETTLEMENT_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import {
  isPerUnitSettlement,
  mapShareFormToTemplateMutation,
} from 'utils/components/contract-template/distribution/settlementType.utils';
import { mapCustomSectionsFromItems } from 'utils/components/contract-template/section/customSection.utils';
import { agreementTemplateFormToQuery } from './agreement-template/agreementTemplateFormToQuery';

interface ISeparateContractTreeItems {
  customSections: ICustomSectionTemplateMutation[];
  agreements: IAgreementTemplatetAggregator[];
  digitalDistribution?: IDistributionMutation;
  physicalDistribution?: IDistributionMutation;
  merchDistribution?: IDistributionMutation;
  channelDistribution?: IDistributionMutation;
}

export const getDistributionWithShares = (
  items: IContractTemplateTreeItemForm[],
  type: IDistributionType
): IDistributionMutation | undefined => {
  const itemsWithPosition = items.map((items, index) => ({
    ...items,
    position: index,
  }));

  const distributionItem = itemsWithPosition.find(item => item.type === type);

  if (!distributionItem || distributionItem.type !== type) {
    return undefined;
  }

  const settlementType =
    distributionItem.settlementType ?? SETTLEMENT_TYPE.Percentage;

  let defaultShares = 0;
  let defaultAmountPerUnit = 0;

  const distributionShares =
    distributionItem.children.reduce<IDistributionSharesMutation[]>(
      (acc, item: IContractTemplateTreeItemForm) => {
        if (
          item.type !== TEMPLATE_TYPE.agreement ||
          !item.content.distributionTemplateShares
        ) {
          return acc;
        }

        if (item.content.type === AGREEMENT_TYPE.defaultShare) {
          if (isPerUnitSettlement(settlementType)) {
            defaultAmountPerUnit =
              item.content.distributionTemplateShares.amountPerUnit ?? 0;
          } else {
            defaultShares =
              item.content.distributionTemplateShares.percent ?? 0;
          }
          return acc;
        }

        if (item.content.type === AGREEMENT_TYPE.shares) {
          const { id: salesChannelId, percent, amountPerUnit } =
            item.content.distributionTemplateShares;

          acc.push(
            mapShareFormToTemplateMutation(settlementType, salesChannelId, {
              percent,
              amountPerUnit,
            })
          );
        }

        return acc;
      },
      []
    );

  return {
    title: distributionItem.content,
    position: distributionItem.position,
    settlementType,
    ...(isPerUnitSettlement(settlementType)
      ? { defaultAmountPerUnit }
      : { defaultShares }),
    distributionTemplateShares: distributionShares,
  };
};

export const separateTemplateTreeItems = (
  items: IContractTemplateTreeItemForm[]
): ISeparateContractTreeItems => {
  const itemsWithPosition = items.map((items, index) => ({
    ...items,
    position: index,
  }));

  const agreements: IAgreementTemplatetAggregator[] = [];
  for (const item of itemsWithPosition) {
    if (item.type === TEMPLATE_TYPE.agreement) {
      agreements.push(
        agreementTemplateFormToQuery(item.content, item.position, true)
      );
    }
  }

  const customSections = mapCustomSectionsFromItems(itemsWithPosition);
  const digital = getDistributionWithShares(
    items,
    TEMPLATE_TYPE.digitalDistribution
  );
  const physical = getDistributionWithShares(
    items,
    TEMPLATE_TYPE.physicalDistribution
  );
  const merch = getDistributionWithShares(
    items,
    TEMPLATE_TYPE.merchDistribution
  );
  const youtube = getDistributionWithShares(
    items,
    TEMPLATE_TYPE.channelDistribution
  );
  return {
    customSections,
    agreements,
    ...(digital || physical || merch || youtube
      ? {
          digitalDistribution: digital,
          physicalDistribution: physical,
          merchDistribution: merch,
          channelDistribution: youtube,
        }
      : {}),
  };
};
